import { createContext, ReactNode, useMemo } from 'react';

import { Directory } from '@core/types/types.common';
import { DBUserSimple } from '@core/types/types.db';
import { filterChannels } from '@core/util/util.filterChannels';
import { useRequiredContext } from '@frontend/shared/hooks/useRequiredContext';

export const DirectoryContext = createContext<Directory | undefined>(undefined);

export interface DirectoryProviderProps {
  children: ReactNode;
  directory: Directory;
}

export const makeUnknownUser = (userId: string): DBUserSimple => ({
  id: userId,
  name: 'Unknown User',
  first_name: 'Unknown',
  image: '',
  expert_channels: [],
  is_slack_deleted: undefined,
});

export const makeUnverifiedUser = (): DBUserSimple => ({
  id: 'UNVERIFIED',
  name: 'Unverified',
  first_name: 'Unverified',
  image: '/assets/unverified.png',
  expert_channels: [],
  is_slack_deleted: undefined,
});

export function useDirectoryMe() {
  return useRequiredContext(DirectoryContext).me;
}

export function useDirectoryTeam() {
  return useRequiredContext(DirectoryContext).team;
}

export function useDirectoryChannel(channelId?: string) {
  const directory = useRequiredContext(DirectoryContext);
  return directory.channels.find((channel) => channel.id === channelId);
}

export function useDirectoryChannels(
  options: Parameters<typeof filterChannels>[1],
) {
  const directory = useRequiredContext(DirectoryContext);

  return useMemo(
    () => filterChannels(directory.channels, options),
    [directory.channels, options],
  );
}

export function useDirectoryUser(userId?: string) {
  const directory = useRequiredContext(DirectoryContext);

  return useMemo(() => {
    if (!userId) return null;
    if (userId === 'UNVERIFIED') return makeUnverifiedUser();

    const user = directory.users.find((user) => userId === user.id);
    return user;
  }, [userId, directory]);
}

export function useDirectoryUsers(userIds?: string[]) {
  const directory = useRequiredContext(DirectoryContext);

  const userIdsString = userIds?.join(',') ?? '';
  return useMemo(() => {
    if (!directory.team) return [];

    const users = directory.users;

    if (!userIds) return users;

    return userIds.map((userId) => {
      if (userId === 'UNVERIFIED') return makeUnverifiedUser();
      const foundUser = users.find((user) => userId === user.id);

      if (!foundUser) {
        return makeUnknownUser(userId);
      }
      return foundUser;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdsString, directory]);
}

export function useDirectoryCategory(categoryId?: string) {
  const directory = useRequiredContext(DirectoryContext);
  return directory.categories.find((category) => category.id === categoryId);
}

export function useDirectoryCategories() {
  const directory = useRequiredContext(DirectoryContext);
  return directory.categories;
}
